import { inject, Injectable } from '@angular/core';
import type { Session } from '@supabase/supabase-js';
import { catchError, from, map, Observable, of } from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import { GetProfileGQL } from '../graphql/generated/services';
import type { Profile } from '../models/profile.model';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);
  private readonly getProfileGql = inject(GetProfileGQL);

  /** 現在のユーザーの profile を取得する。 */
  getCurrentProfile(userId: string): Observable<Profile | null> {
    return this.getProfileGql
      .fetch({
        variables: { id: userId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          if (result.error) return null;
          const node = result.data?.profilesCollection?.edges?.[0]?.node;
          if (!node?.id) return null;
          return {
            id: node.id,
            display_name: node.display_name ?? null,
            email: node.email ?? null,
            preferred_language: node.preferred_language ?? null,
            avatar_path: node.avatar_path ?? null,
            avatar_updated_at:
              node.avatar_updated_at != null
                ? String(node.avatar_updated_at)
                : null,
          };
        }),
        catchError(() => of(null)),
      );
  }

  /**
   * セッションの Auth 情報で profiles を upsert する。
   * ログイン成功のたびに呼ぶ。
   * 既存の display_name があれば最優先で維持し、未設定時のみ user_metadata.full_name を初期値として使う。
   */
  async syncProfileFromAuth(session: Session): Promise<void> {
    const user = session.user;
    const fullName = user.user_metadata?.['full_name'] as string | undefined;
    const { error } = await this.supabase.rpc('sync_profile_from_auth', {
      p_email: user.email ?? null,
      p_full_name: fullName?.trim() || null,
    });
    if (error) throw error;
  }

  /** 表示名を保存する。認証済みユーザーが自分の profile を更新。 */
  upsertDisplayName(displayName: string): Observable<void> {
    return from(
      this.supabase.rpc('update_profile_display_name', {
        p_display_name: displayName,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
    );
  }

  /** UI 言語を保存する。検証はアプリ側。 */
  upsertPreferredLanguage(language: string | null): Observable<void> {
    return from(
      this.supabase.rpc('update_profile_preferred_language', {
        p_preferred_language: language,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
    );
  }
}
